import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import { orderAPI } from '../services/api';
import './OrderDetail.css';
import { useDocumentMeta } from '../seo/useDocumentMeta';

const OrderDetail = () => {
  useDocumentMeta();
  const { orderNumber } = useParams();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderNumber) {
        setError('No order number provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await orderAPI.getOrder(orderNumber);
        
        if (result.success) {
          setOrder(result.data);
        } else {
          setError(result.message || 'Failed to fetch order details');
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderNumber]);

  if (loading) {
    return (
      <div className="order-detail-page">
        <div className="loading-container">
          <h2>Loading...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-detail-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <Link to="/profile" className="back-button">Back to Profile</Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-detail-page">
        <div className="error-container">
          <h2>Order Not Found</h2>
          <p>The order you're looking for doesn't exist.</p>
          <Link to="/profile" className="back-button">Back to Profile</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-detail-page">
      <div className="order-detail-container">
        <div className="order-header">
          <Link to="/profile" className="back-link">← Back to Orders</Link>
          <h1>Order #{order.orderNumber}</h1>
          <div className="order-status">
            <span className={`status-badge status-${order.status.toLowerCase()}`}>
              {order.status}
            </span>
          </div>
        </div>

        <div className="order-content">
          <div className="order-section">
            <h2>Order Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Order Date</label>
                <p>{new Date(order.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="info-item">
                <label>Order Number</label>
                <p>{order.orderNumber}</p>
              </div>
              <div className="info-item">
                <label>Email</label>
                <p>{order.email}</p>
              </div>
              <div className="info-item">
                <label>Status</label>
                <p className={`status-${order.status.toLowerCase()}`}>{order.status}</p>
              </div>
            </div>
          </div>

          <div className="order-section">
            <h2>Shipping Address</h2>
            <div className="address-info">
              <p><strong>{order.shippingAddress.firstName} {order.shippingAddress.lastName}</strong></p>
              <p>{order.shippingAddress.address}</p>
              {order.shippingAddress.apartment && <p>{order.shippingAddress.apartment}</p>}
              <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}</p>
              <p>{order.shippingAddress.country}</p>
              <p>{order.shippingAddress.phone}</p>
            </div>
          </div>

          <div className="order-section">
            <h2>Payment Information</h2>
            <div className="payment-info">
              <p><strong>Payment Method:</strong> {order.payment?.method || 'Card'}</p>
              {order.payment?.cardBrand && (
                <p><strong>Card:</strong> {order.payment.cardBrand} ending in {order.payment.cardLastFour}</p>
              )}
              {order.payment?.stripePaymentIntentId && (
                <p><strong>Transaction ID:</strong> {order.payment.stripePaymentIntentId}</p>
              )}
            </div>
          </div>

          <div className="order-section">
            <h2>Order Items</h2>
            <div className="items-list">
              {order.items.map((item, index) => (
                <div key={index} className="item-row">
                  <img src={item.images && item.images.length > 0 ? item.images[0] : item.image || ''} alt={item.name} className="item-image" />
                  <div className="item-details">
                    <h3>{item.name}</h3>
                    <p>Quantity: {item.quantity}</p>
                    <p>Price: ${item.price.toFixed(2)} each</p>
                  </div>
                  <div className="item-total">
                    ${(item.price * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="order-section">
            <h2>Order Summary</h2>
            <div className="order-summary">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>${order.pricing?.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="summary-row">
                <span>Shipping</span>
                <span>{order.pricing?.shipping === 0 ? 'Free' : `$${order.pricing?.shipping?.toFixed(2) || '0.00'}`}</span>
              </div>
              <div className="summary-row">
                <span>Tax</span>
                <span>${order.pricing?.tax?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>${order.pricing?.total?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
